import { Link } from 'react-router-dom';

export default function LittlesLawPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-cyan-600 via-blue-500 to-indigo-600 text-white py-32 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-5xl mx-auto relative z-10">
          <Link 
            to="/theory" 
            className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-8 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            <span>理論一覧に戻る</span>
          </Link>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            リトルの法則
          </h1>
          <p className="text-2xl md:text-3xl font-light leading-relaxed opacity-95">
            待ち時間は「人手不足」ではなく「入れすぎ」で決まる
          </p>
          <div className="mt-8 text-xl opacity-90">
            ― 薬局の混雑は、数式で説明できる ―
          </div>
        </div>
      </section>

      {/* Conclusion */}
      <section className="py-16 px-6 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-10 border-l-8 border-orange-500">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
              結論
            </h2>
            <p className="text-xl leading-relaxed text-gray-800">
              調剤薬局の待ち時間は、「忙しいから」「人が足りないから」ではありません。<strong className="text-orange-600">入れている仕事量（仕掛品）が多すぎることが原因</strong>です。これを説明できるのが <strong className="text-cyan-600">リトルの法則</strong> です。
            </p>
          </div>
        </div>
      </section>

      {/* Theory Explanation */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">理論の解説｜リトルの法則とは何か</h2>
          
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              <strong className="text-cyan-600">リトルの法則（Little's Law）</strong>は、待ち行列理論（キューイング理論）の中核となる数式です。提唱者は <strong>ジョン・リトル</strong>。
            </p>
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              数式はこれだけです。
            </p>
          </div>

          {/* Formula */}
          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-12 mb-12 border-2 border-cyan-200">
            <div className="text-center mb-8">
              <div className="text-6xl font-bold text-cyan-700 mb-6">
                L = λ × W
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="text-3xl font-bold text-cyan-600 mb-3">L</div>
                <div className="text-gray-800 font-semibold mb-2">システム内の滞留量</div>
                <div className="text-gray-600">薬局内にある処方箋の数＝WIP</div>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="text-3xl font-bold text-blue-600 mb-3">λ</div>
                <div className="text-gray-800 font-semibold mb-2">到着率（ラムダ）</div>
                <div className="text-gray-600">1時間あたりに来る処方箋枚数</div>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="text-3xl font-bold text-indigo-600 mb-3">W</div>
                <div className="text-gray-800 font-semibold mb-2">滞在時間</div>
                <div className="text-gray-600">受付から薬を受け取るまでの平均時間</div>
              </div>
            </div>

            <div className="mt-8 bg-cyan-100 rounded-xl p-6">
              <p className="text-lg text-gray-800 font-semibold">
                <i className="ri-lightbulb-line text-cyan-600 mr-2"></i>
                重要なのは、👉 <strong className="text-cyan-700">到着や処理のバラつきに関係なく成立する</strong>という点です。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Numerical Example */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">数字で一気に腹落ちさせる</h2>
          
          <div className="bg-white rounded-2xl shadow-xl p-10 mb-8">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">たとえば、</h3>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-32 text-gray-600 font-semibold">到着率：</div>
                  <div className="text-xl text-gray-800">12枚／時（5分に1枚）</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-32 text-gray-600 font-semibold">平均滞在時間：</div>
                  <div className="text-xl text-gray-800">30分＝0.5時</div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-cyan-100 to-blue-100 rounded-xl p-8 mb-6">
                <div className="text-center">
                  <div className="text-lg text-gray-700 mb-4">このとき、</div>
                  <div className="text-4xl font-bold text-cyan-700 mb-4">
                    L = 12 × 0.5 = 6
                  </div>
                </div>
              </div>

              <p className="text-xl text-gray-700 leading-relaxed">
                つまり、<strong className="text-cyan-600">常に6枚分の処方箋が「待ち」として滞留している</strong>という意味になります。
              </p>
            </div>

            <div className="bg-orange-50 rounded-xl p-8 border-l-4 border-orange-500">
              <p className="text-xl text-gray-800 leading-relaxed">
                逆に言えば、<br />
                <strong className="text-orange-600 text-2xl">待ち時間を半分にしたければ→ 滞留量（WIP）を半分にするしかない</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Common Misconceptions */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">薬局で起きている「典型的な誤解」</h2>
          
          <div className="mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              多くの薬局では、こう考えがちです。
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="bg-red-50 rounded-xl p-6 border-2 border-red-200">
                <div className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-3xl text-red-500 mt-1"></i>
                  <div>
                    <p className="text-lg text-gray-800">「混んでいるから、とりあえず受ける」</p>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 rounded-xl p-6 border-2 border-red-200">
                <div className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-3xl text-red-500 mt-1"></i>
                  <div>
                    <p className="text-lg text-gray-800">「作れるときに作っておこう」</p>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 rounded-xl p-6 border-2 border-red-200">
                <div className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-3xl text-red-500 mt-1"></i>
                  <div>
                    <p className="text-lg text-gray-800">「一包化は後回しでいい」</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-10 border-l-8 border-red-500">
              <p className="text-xl text-gray-800 leading-relaxed mb-6">
                しかしリトルの法則で見ると、これはすべて同じ問題です。
              </p>
              <p className="text-2xl font-bold text-red-600 mb-8">
                👉 WIPを増やしている。
              </p>

              <div className="bg-white rounded-xl p-8">
                <p className="text-lg text-gray-700 mb-4 font-semibold">WIPが増えると、</p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <i className="ri-arrow-right-s-line text-red-500"></i>
                    <span className="text-gray-800">待ち時間が伸びる</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-arrow-right-s-line text-red-500"></i>
                    <span className="text-gray-800">鑑査・投薬が詰まる</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-arrow-right-s-line text-red-500"></i>
                    <span className="text-gray-800">集中力が落ちる</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-arrow-right-s-line text-red-500"></i>
                    <span className="text-gray-800">誤薬リスクが上がる</span>
                  </div>
                </div>
                <p className="text-lg text-gray-700 mt-6">
                  という<strong className="text-red-600">"連鎖"</strong>が必ず起きます。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two Interventions */}
      <section className="py-20 px-6 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">調剤薬局への翻訳｜介入は2つしかない</h2>
          
          <div className="mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              リトルの法則が示す事実は、非常に厳密です。
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl shadow-lg p-8 border-t-4 border-blue-500">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-blue-600">①</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Wを短くする</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-xl text-blue-500 mt-1"></i>
                    <span className="text-gray-700">ボトルネック工程の能力を上げる</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-xl text-blue-500 mt-1"></i>
                    <span className="text-gray-700">ムダな動線・割り込みを減らす</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-8 border-t-4 border-cyan-500">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-cyan-600">②</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">L（WIP）を制限する</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-xl text-cyan-500 mt-1"></i>
                    <span className="text-gray-700">入れすぎない</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-xl text-cyan-500 mt-1"></i>
                    <span className="text-gray-700">作りすぎない</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-xl text-cyan-500 mt-1"></i>
                    <span className="text-gray-700">溜めすぎない</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 bg-gradient-to-r from-orange-100 to-amber-100 rounded-xl p-8 border-l-4 border-orange-500">
              <p className="text-2xl font-bold text-gray-900">
                👉 これ以外の近道は存在しません。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Heavy Prescriptions */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">「重い処方」を後回しにすると、なぜ詰まるのか</h2>
          
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl shadow-xl p-10">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              一包化・麻薬・在宅分包・散剤監査など、時間のかかる処方を後回しにすると、
            </p>

            <div className="space-y-6 mb-8">
              <div className="bg-white rounded-xl p-6 border-l-4 border-red-400">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-arrow-down-line text-xl text-red-600"></i>
                  </div>
                  <p className="text-lg text-gray-800">見えないWIPが積み上がる</p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border-l-4 border-orange-400">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-arrow-down-line text-xl text-orange-600"></i>
                  </div>
                  <p className="text-lg text-gray-800">ある瞬間に"塊"として出てくる</p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border-l-4 border-red-600">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-alert-line text-xl text-red-600"></i>
                  </div>
                  <p className="text-lg text-gray-800 font-semibold">全体が一気に止まる</p>
                </div>
              </div>
            </div>

            <div className="bg-red-100 rounded-xl p-8 border-2 border-red-300">
              <p className="text-xl text-gray-900 font-bold">
                👉 見かけ上ラクでも、<span className="text-red-600">リトルの法則的には最悪の選択</span>です。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Operational Rules */}
      <section className="py-20 px-6 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">使える運用ルール（WIP制限の型）</h2>
          
          <div className="space-y-6 mb-8">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-stop-circle-line text-3xl text-green-600"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">WIP上限ルール</h3>
                  <p className="text-lg text-gray-700">調剤待ちが◯枚を超えたら新規投入を止める</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-play-circle-line text-3xl text-blue-600"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">早期着手</h3>
                  <p className="text-lg text-gray-700">一包化は最初の5分だけでも流し始める</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-layout-column-line text-3xl text-purple-600"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">レーン分け</h3>
                  <p className="text-lg text-gray-700">短工程／長工程／在宅・施設（バッチ処理）</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-cyan-100 to-blue-100 rounded-xl p-8 border-l-4 border-cyan-500">
            <p className="text-xl text-gray-900">
              👉 これは<Link to="/theory/dbr" className="text-cyan-600 hover:text-cyan-700 font-bold underline">DBR（ドラム・バッファ・ロープ）</Link>の実装です。
            </p>
          </div>
        </div>
      </section>

      {/* KPI */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">KPI｜リトルの法則で見るべき指標</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 border-2 border-cyan-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-cyan-500 rounded-lg flex items-center justify-center">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">平均滞在時間 W（分）</h3>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border-2 border-blue-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                  <i className="ri-file-list-3-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">WIP（調剤待ち枚数）</h3>
              </div>
            </div>

            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border-2 border-indigo-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-indigo-500 rounded-lg flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">到着率 λ（時間帯別）</h3>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border-2 border-purple-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                  <i className="ri-speed-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">工程別処理能力（枚／時）</h3>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-8 border-l-4 border-orange-500">
            <p className="text-xl text-gray-900">
              これだけで、<strong className="text-orange-600">「なぜ今日は荒れたか」が説明できます</strong>。
            </p>
          </div>
        </div>
      </section>

      {/* Tomorrow's Action */}
      <section className="py-20 px-6 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">明日の一手｜最初の改善</h2>
          
          <div className="bg-white rounded-2xl shadow-xl p-10">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              明日やることは、これだけです。
            </p>

            <div className="bg-gradient-to-r from-orange-100 to-amber-100 rounded-xl p-10 mb-8 border-l-8 border-orange-500">
              <p className="text-2xl font-bold text-gray-900 mb-4">
                👉 「今、調剤待ちは何枚か？」を全員で共有する。
              </p>
              <p className="text-xl text-gray-700">
                ホワイトボードに数字を書く。それだけで、
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4 bg-green-50 rounded-lg p-4">
                <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                <span className="text-lg text-gray-800">入れすぎ</span>
              </div>
              <div className="flex items-center gap-4 bg-green-50 rounded-lg p-4">
                <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                <span className="text-lg text-gray-800">作りすぎ</span>
              </div>
              <div className="flex items-center gap-4 bg-green-50 rounded-lg p-4">
                <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                <span className="text-lg text-gray-800">事故につながる忙しさ</span>
              </div>
            </div>

            <p className="text-xl text-gray-700 mt-8">
              が、<strong className="text-orange-600">目に見えて減ります</strong>。
            </p>
          </div>
        </div>
      </section>

      {/* Related Theories */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">関連する理論</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link 
              to="/theory/toc" 
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-cyan-500"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-3">TOC（制約理論）</h3>
              <p className="text-gray-600 mb-4">全体を決める制約を見つける</p>
              <div className="flex items-center gap-2 text-cyan-600 font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>

            <Link 
              to="/theory/dbr" 
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-cyan-500"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-3">DBR</h3>
              <p className="text-gray-600 mb-4">入れすぎを止める実装技術</p>
              <div className="flex items-center gap-2 text-cyan-600 font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>

            <Link 
              to="/theory/flow-efficiency" 
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-cyan-500"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-3">フロー効率</h3>
              <p className="text-gray-600 mb-4">待ち時間を減らす視点</p>
              <div className="flex items-center gap-2 text-cyan-600 font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>

          <div className="mt-8 text-center">
            <Link 
              to="/theory" 
              className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-semibold text-lg"
            >
              <i className="ri-arrow-left-line"></i>
              <span>理論一覧に戻る</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
